import { platformIdsOf, primaryPlatformLinks } from './snapshot.js';
import { REQUIRED_SPLIT_TOTAL_HUNDREDTHS, formatPercent, parsePercent, sumPercent, } from './percent.js';
/** Additional impacted Platforms permitted beyond the primary (R4.4). */
export const MAX_ADDITIONAL_PLATFORMS = 9;
/** Total Platforms permitted per Initiative: one primary plus the additional ones. */
export const MAX_PLATFORMS = MAX_ADDITIONAL_PLATFORMS + 1;
/**
 * Stable rule identifiers (R8.1). `HV-005` is the Platform-split rule R8.3 names
 * among the six non-configurable hard rules; the structural rules of R4.4 are
 * reported under their own identifier so a message about cardinality is not
 * confused with a message about a total.
 */
export const PLATFORM_SET_STRUCTURE_RULE_ID = 'HV-007';
export const PLATFORM_SPLIT_RULE_ID = 'HV-005';
/** Field paths reported with a finding, as the intake form addresses them. */
export const PLATFORM_SET_FIELD = 'platforms';
export const PLATFORM_SPLIT_FIELD = 'platforms.splitPercent';
/** i18n message keys per violation kind (R18.16): no user-facing text lives in code. */
export const PLATFORM_SET_MESSAGE_KEYS = {
    no_primary_platform: 'validation.platformSet.noPrimaryPlatform',
    multiple_primary_platforms: 'validation.platformSet.multiplePrimaryPlatforms',
    duplicate_platform: 'validation.platformSet.duplicatePlatform',
    too_many_platforms: 'validation.platformSet.tooManyPlatforms',
    split_incomplete: 'validation.platformSplit.incomplete',
    split_malformed: 'validation.platformSplit.malformed',
    split_too_many_decimal_places: 'validation.platformSplit.tooManyDecimalPlaces',
    split_out_of_range: 'validation.platformSplit.outOfRange',
    split_total_mismatch: 'validation.platformSplit.totalMismatch',
};
/** The i18n key for a violation kind. */
export function platformSetMessageKey(kind) {
    return PLATFORM_SET_MESSAGE_KEYS[kind];
}
/** Platform identifiers that appear more than once in the submitted set (R4.4). */
function duplicatePlatformIds(links) {
    const seen = new Set();
    const duplicates = new Set();
    for (const link of links) {
        if (seen.has(link.platformId)) {
            duplicates.add(link.platformId);
        }
        seen.add(link.platformId);
    }
    return [...duplicates];
}
/** Members carrying an entered split value. */
function withSplit(links) {
    return links.filter((link) => link.splitPercent !== null);
}
/**
 * Validates the Platform set of a submitted Initiative.
 *
 * Accepts exactly the sets R4.4 and R4.5 permit: one primary Platform, at most
 * {@link MAX_ADDITIONAL_PLATFORMS} additional ones, each Platform at most once,
 * and either no split at all or a complete split of two-decimal values in 0.01
 * to 100.00 totalling exactly 100.00. Every other set is rejected with the full
 * list of what is wrong with it.
 */
export function validatePlatformSet(links) {
    const violations = [];
    const structural = { ruleId: PLATFORM_SET_STRUCTURE_RULE_ID, fields: [PLATFORM_SET_FIELD] };
    const split = { ruleId: PLATFORM_SPLIT_RULE_ID, fields: [PLATFORM_SPLIT_FIELD] };
    const primaries = primaryPlatformLinks(links);
    const primary = primaries.length === 1 ? primaries[0] : undefined;
    if (primaries.length === 0) {
        violations.push({ ...structural, kind: 'no_primary_platform' });
    }
    else if (primaries.length > 1) {
        violations.push({
            ...structural,
            kind: 'multiple_primary_platforms',
            platformIds: primaries.map((link) => link.platformId),
        });
    }
    const duplicates = duplicatePlatformIds(links);
    if (duplicates.length > 0) {
        violations.push({ ...structural, kind: 'duplicate_platform', platformIds: duplicates });
    }
    const distinctIds = platformIdsOf(links);
    if (distinctIds.length > MAX_PLATFORMS) {
        violations.push({
            ...structural,
            kind: 'too_many_platforms',
            platformIds: distinctIds,
            platformCount: distinctIds.length,
            maximumPlatformCount: MAX_PLATFORMS,
        });
    }
    const entered = withSplit(links);
    const splitEntered = entered.length > 0;
    let splitTotal = null;
    // R4.5 is conditional: no split entered means no split rule applies.
    if (splitEntered) {
        const unset = links.filter((link) => link.splitPercent === null);
        if (unset.length > 0) {
            violations.push({
                ...split,
                kind: 'split_incomplete',
                platformIds: unset.map((link) => link.platformId),
            });
        }
        const malformed = [];
        const imprecise = [];
        const outOfRange = [];
        const parsed = [];
        for (const link of entered) {
            const result = parsePercent(link.splitPercent);
            if (result.ok) {
                parsed.push(result.hundredths);
                continue;
            }
            switch (result.failure) {
                case 'malformed':
                    malformed.push(link);
                    break;
                case 'too_many_decimal_places':
                    imprecise.push(link);
                    break;
                case 'out_of_range':
                    outOfRange.push(link);
                    break;
            }
        }
        const valueViolation = (kind, offenders) => {
            if (offenders.length === 0) {
                return;
            }
            violations.push({
                ...split,
                kind,
                platformIds: offenders.map((link) => link.platformId),
                enteredValues: offenders.map((link) => link.splitPercent ?? ''),
            });
        };
        valueViolation('split_malformed', malformed);
        valueViolation('split_too_many_decimal_places', imprecise);
        valueViolation('split_out_of_range', outOfRange);
        // The total is only meaningful once every entered value is a percentage and
        // every member carries one; reporting a total computed from a partial or
        // unparseable split would name a number the user cannot reconcile.
        const total = sumPercent(parsed);
        splitTotal = total;
        if (unset.length === 0 &&
            malformed.length === 0 &&
            imprecise.length === 0 &&
            outOfRange.length === 0 &&
            total !== REQUIRED_SPLIT_TOTAL_HUNDREDTHS) {
            violations.push({
                ...split,
                kind: 'split_total_mismatch',
                platformIds: entered.map((link) => link.platformId),
                enteredTotal: formatPercent(total),
                requiredTotal: formatPercent(REQUIRED_SPLIT_TOTAL_HUNDREDTHS),
            });
        }
    }
    const [first, ...rest] = violations;
    if (first !== undefined) {
        return { valid: false, violations: [first, ...rest] };
    }
    if (primary === undefined) {
        // Unreachable: a primary count other than one is reported above, so the
        // violation list would not have been empty. Written out rather than asserted
        // so the function stays total without a thrown error.
        return { valid: false, violations: [{ ...structural, kind: 'no_primary_platform' }] };
    }
    return {
        valid: true,
        primaryPlatformId: primary.platformId,
        additionalPlatformIds: links.filter((link) => !link.isPrimary).map((link) => link.platformId),
        splitEntered,
        splitTotal,
    };
}
//# sourceMappingURL=platform-set.js.map